import React, { useMemo, useRef, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import './index.scss';
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	WarningOutlined,
	EditOutlined,
} from '@ant-design/icons';
import { Tooltip, Skeleton } from 'antd';
import { DeleteActionSvg } from 'utils/svgs';
import Ellipse from '../Ellipse';

interface Column {
	title: string;
	key: string;
	dataIndex?: string;
	width?: number;
}

interface TableRowProps {
	index: number;
	style: React.CSSProperties;
	data: {
		records: any[];
		columns: Column[];
		onDelete: (index: number) => void;
		onErrorClick: (record: any) => void;
		getClassName: (isValid: boolean) => string;
		NrcUpdateObject?: any;
	};
	isScrolling?: boolean;
	renderedRowsRef: React.MutableRefObject<Set<number>>;
}

interface SimpleVirtualTableProps {
	validationResults: any[];
	columns: Column[];
	onDelete: (index: number) => void;
	onErrorClick: (record: any) => void;
	getClassName: (isValid: boolean) => string;
	NrcUpdateObject?: any;
}

// Helper functions
const isRecordUpdatable = (record: any, NrcUpdateObject: any) => {
	if (!NrcUpdateObject || !record['National Registration Number'])
		return false;
	const matchingEntry = NrcUpdateObject.find(
		(item: any) =>
			Object.keys(item)[0] === record['National Registration Number']
	);
	return (
		matchingEntry &&
		matchingEntry[record['National Registration Number']]?.isUpdated ===
			true
	);
};

const isStaffCodeChange = (record: any, NrcUpdateObject: any) => {
	if (!NrcUpdateObject || !record['National Registration Number'])
		return false;
	const matchingEntry = NrcUpdateObject.find(
		(item: any) =>
			Object.keys(item)[0] === record['National Registration Number']
	);
	return (
		matchingEntry &&
		matchingEntry[record['National Registration Number']]
			?.isStaffCodeChange === true
	);
};

// Simple row component
const TableRow: React.FC<TableRowProps> = ({
	index,
	style,
	data,
	isScrolling,
	renderedRowsRef,
}) => {
	const {
		records,
		columns,
		onDelete,
		onErrorClick,
		getClassName,
		NrcUpdateObject,
	} = data;
	const record = records[index];

	if (!record) return null;

	const showSkeleton = isScrolling && !renderedRowsRef.current.has(index);
	if (!showSkeleton) renderedRowsRef.current.add(index);

	return (
		<div style={style} className="simple-virtual-row">
			{columns.map((column: any, colIndex) => {
				if (showSkeleton) {
					return (
						<div
							key={colIndex}
							className="simple-virtual-cell"
							style={{ width: column.width || 150 }}
						>
							<Skeleton.Input
								active
								size="small"
								style={{ width: column.width || 150 }}
							/>
						</div>
					);
				}

				let content: React.ReactNode = '';

				if (column.key === '_validationStatus') {
					content = record._isValid ? (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: '#52c41a',
							}}
							className="bg-white"
						>
							<CheckCircleOutlined />
						</div>
					) : (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: '#ff4d4f',
							}}
							className="bg-white"
						>
							<CloseCircleOutlined />
						</div>
					);
				} else if (column.key === '_errors') {
					content = !record._isValid ? (
						<Tooltip title="View validation errors">
							<div
								onClick={() => onErrorClick(record)}
								style={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									color: '#faad14',
									cursor: 'pointer',
								}}
								className="cursor-pointer bg-white"
							>
								<WarningOutlined />
							</div>
						</Tooltip>
					) : null;
				} else if (column.key === 'action') {
					content = (
						<Tooltip title="Delete">
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									cursor: 'pointer',
								}}
								className="cursor-pointer bg-white"
								onClick={() => onDelete(index)}
							>
								<DeleteActionSvg />
							</div>
						</Tooltip>
					);
				} else {
					const text = record[column.dataIndex] || '';
					const isFirstNameWithEdit =
						column.key === 'First Name' &&
						isRecordUpdatable(record, NrcUpdateObject);
					const isStaffCodeWithWarning =
						column.key === 'Staff Code' &&
						isStaffCodeChange(record, NrcUpdateObject);

					content = (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								justifyContent: 'flex-start',
								paddingLeft: '8px',
							}}
							className="bg-white"
						>
							<Ellipse
								maxLength={getMaxLength(column.width || 150)}
								message={text}
								isTooltip={true}
								tooltipMessage={text}
								key={`${index}-${column.key}-${text}`}
							/>
							{isFirstNameWithEdit && (
								<Tooltip title="This record will be updated due to matching NRC">
									<EditOutlined
										style={{
											marginLeft: 8,
											color: '#584495',
											fontSize: '14px',
										}}
									/>
								</Tooltip>
							)}
							{isStaffCodeWithWarning && (
								<Tooltip
									title={`This employee is currently ${
										text?.toLowerCase() === 'hourly'
											? 'Monthly'
											: 'Hourly'
									}. Changing the Staff Code will switch them to ${
										text?.toLowerCase() === 'hourly'
											? 'Hourly'
											: 'Monthly'
									}, and all calculations will update accordingly.`}
								>
									<WarningOutlined
										style={{
											marginLeft: 8,
											color: '#faad14',
											fontSize: '14px',
										}}
									/>
								</Tooltip>
							)}
						</div>
					);
				}

				return (
					<div
						key={colIndex}
						className={`simple-virtual-cell bg-white ${getClassName(
							record._isValid
						)}`}
						style={{ width: column.width || 150 }}
					>
						{content}
					</div>
				);
			})}
		</div>
	);
};

// Helper function to calculate max length for Ellipse based on column width
const getMaxLength = (width: number): number => {
	if (width <= 100) return 8;
	if (width <= 150) return 15;
	if (width <= 200) return 25;
	return 35;
};

// Simple virtual table component
const SimpleVirtualTable: React.FC<SimpleVirtualTableProps> = ({
	validationResults,
	columns,
	onDelete,
	onErrorClick,
	getClassName,
	NrcUpdateObject,
}) => {
	const headerRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const renderedRowsRef = useRef<Set<number>>(new Set());

	const tableData = useMemo(
		() => ({
			records: validationResults,
			columns,
			onDelete,
			onErrorClick,
			getClassName,
			NrcUpdateObject,
		}),
		[
			validationResults,
			columns,
			onDelete,
			onErrorClick,
			getClassName,
			NrcUpdateObject,
		]
	);

	const totalWidth = useMemo(
		() => columns.reduce((sum, col) => sum + (col.width || 150), 0),
		[columns]
	);

	const handleScroll = useCallback((event: any) => {
		if (headerRef.current && scrollContainerRef.current) {
			headerRef.current.scrollLeft =
				scrollContainerRef.current.scrollLeft;
		}
	}, []);

	return (
		<div
			className="simple-virtual-table"
			style={{ display: 'flex', flexDirection: 'column', height: 'auto' }}
		>
			{/* Header */}
			<div
				ref={headerRef}
				className="simple-virtual-header-container"
				style={{ overflowX: 'hidden', flexShrink: 0 }}
			>
				<div
					className="simple-virtual-header"
					style={{ width: totalWidth }}
				>
					{columns.map((column, index) => (
						<div
							key={index}
							className="simple-virtual-header-cell"
							style={{ width: column.width || 150 }}
						>
							{column.title}
						</div>
					))}
				</div>
			</div>

			{/* Virtual List */}
			<div
				ref={scrollContainerRef}
				style={{ overflowX: 'auto', flexShrink: 0 }}
				className="container"
				onScroll={handleScroll}
			>
				<List
					height={300}
					itemCount={validationResults.length}
					itemSize={50}
					itemData={{ ...tableData, renderedRowsRef }}
					width={totalWidth}
					useIsScrolling
					style={{ overflowY: 'auto' }}
				>
					{({ index, style, data, isScrolling }) => (
						<TableRow
							index={index}
							style={style}
							data={data}
							isScrolling={isScrolling}
							renderedRowsRef={data.renderedRowsRef}
						/>
					)}
				</List>
			</div>

			{/* Footer */}
			<div
				className="simple-virtual-footer"
				style={{ marginTop: 0, flexShrink: 0 }}
			>
				Total: {validationResults.length} records
			</div>
		</div>
	);
};

export default SimpleVirtualTable;
